import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaBuilding, FaDownload } from "react-icons/fa";
import { useSelector } from "react-redux";
import moment from "moment";
import styles from "../../schoolCockpit/master/academicConfiguration/Academiclist.module.css";
import payStyles from "./PayFees.module.css";
import {
  CustomButton,
  CustomTextInput,
  PayOnlineModal,
} from "../../../components";
import CustomMRTTable from "../../../components/CustomMRTTable/CustomMRTTable";
import PopupModal from "../../../components/PopupModal/PopupModal";
import AxiosInstance, { TokenService } from "../../../services/Api";
import PayOfflineModal from "../../../components/PayOfflineModal/PayOfflineModal";

const formatCurrency = (value) => {
  const num = Number(value) || 0;
  return `${num < 0 ? "-" : ""}₹${Math.abs(num).toLocaleString("en-IN")}`;
};

const getDiscountValue = (item) => {
  const amount = Number(item.amount) || 0;
  const discountAmount = Number(item.discount_amount) || 0;
  return Number(item.type) === 1
    ? (amount * discountAmount) / 100
    : discountAmount;
};

const PAYMENT_MODE_LABELS = { 1: "Online", 0: "Cash" };
const PAYMENT_MODE_VALUES = { online: 1, cash: 0 };
const PAYMENT_STATUS_LABELS = { 0: "Pending", 1: "Approved", 2: "Rejected" };
const PAYMENT_STATUS_COLORS = { 0: "#B58900", 1: "#1B8A3D", 2: "#D32F2F" };
const PayFees = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { translations } = useSelector((state) => state.language);
  const labels = useMemo(() => translations?.labels || {}, [translations]);

  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const studentId = location.state?.student_id || "";
  const feeId = location.state?.id || "";

  const student_id = clientData?.student_id || studentId || "";
  const school_id = clientData?.school_id || "";
  const academic_year_id = clientData?.academic_year_id || "";
  const slug = String(user?.role?.slug || "").toLowerCase();

  const [loading, setLoading] = useState(false);
  const [feeData, setFeeData] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 5 });
  // Blocks repeated clicks on the receipt download icon until the
  // in-flight download (fetch + print/save flow) finishes.
  const [downloadingId, setDownloadingId] = useState(null);
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
  });
  const [cashAmount, setCashAmount] = useState("");
  const [amountError, setAmountError] = useState("");
  const [formData, setFormData] = useState({ paymentMode: "online" });
  const [cashModalOpen, setCashModalOpen] = useState(false);
  const fetchFeeDetails = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post(
        "schoolFees/student/studentFeeView",
        {
          id: feeId ? Number(feeId) : feeId,
          student_id: Number(student_id),
          school_id: Number(school_id),
          academic_year_id: Number(academic_year_id),
        },
      );
      if (response?.data?.status) {
        setFeeData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching fee details:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentHistory = async () => {
    try {
      const response = await AxiosInstance.post(
        "schoolFees/student/paymentHistory",
        {
          id: feeId ? Number(feeId) : feeId,
          user_id: Number(student_id),
          school_id: Number(school_id),
          academic_year_id: Number(academic_year_id),
        },
      );
      if (response?.data?.status) {
        setPaymentHistory(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching payment history:", error);
    }
  };

  useEffect(() => {
    if (!student_id || !school_id || !academic_year_id) return;
    fetchFeeDetails();
    fetchPaymentHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [student_id, school_id, academic_year_id]);
  const paymentSummary = useMemo(() => {
    const feeDetails = feeData?.fee_details || [];
    const totalFees = feeDetails.reduce(
      (sum, item) => sum + (Number(item.amount) || 0),
      0,
    );
    const discountApplied = feeDetails.reduce(
      (sum, item) => sum + (item.discount_name ? getDiscountValue(item) : 0),
      0,
    );
    const isPastDue = feeData?.due_date
      ? moment().isAfter(moment(feeData.due_date))
      : false;
    const fine = isPastDue ? Number(feeData?.fine_amount) || 0 : 0;
    // Total already paid comes straight from the studentFeeView API response.
    const totalPaid = Number(feeData?.total_paid) || 0;
    const netPayable = totalFees - discountApplied;
    const totalAmountToPay = Math.max(netPayable + fine - totalPaid, 0);
    return {
      dueDate: feeData?.due_date
        ? moment(feeData.due_date).format("DD/MM/YYYY")
        : "-",
      totalFees,
      discountApplied,
      fine,
      totalPaid,
      netPayable,
      totalAmountToPay,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feeData, paymentHistory]);
  /* Seed / reset the cash amount whenever the balance or mode
     changes: prefill to the full balance, clear any error. */
  useEffect(() => {
    if (formData.paymentMode === "cash") {
      setCashAmount(String(paymentSummary.totalAmountToPay || ""));
      setAmountError("");
    }
  }, [formData.paymentMode, paymentSummary.totalAmountToPay]);
  /* ── VALIDATION ─────────────────────────────────────────────
     0 < amount <= outstanding balance. */
  const validateCashAmount = (value) => {
    const raw = String(value).trim();
    if (!raw) return "Enter a valid amount";
    const num = Number(raw);
    if (Number.isNaN(num) || num <= 0) return "Enter a valid amount";
    if (num > paymentSummary.totalAmountToPay) {
      return `Amount cannot exceed ${formatCurrency(
        paymentSummary.totalAmountToPay,
      )}`;
    }
    return "";
  };

  const handleCashAmountChange = (e) => {
    // digits + one decimal only
    const cleaned = e.target.value.replace(/[^\d.]/g, "");
    setCashAmount(cleaned);
    setAmountError(validateCashAmount(cleaned));
  };
  const studentDetails = {
    admission_id: feeData?.admission_id || "",
    student_name: feeData?.student_name || "",
    class_name: feeData?.class_name || "",
    class_id: feeData?.class_id || "",

    section: feeData?.section || "",
  };

  const feesRows = useMemo(() => {
    const feeDetails = feeData?.fee_details || [];
    return feeDetails.map((item) => ({
      id: `fee-${item.fee_type_id}`,
      fee_type: item.fee_type_name,
      amount: Number(item.amount) || 0,
    }));
  }, [feeData]);

  const paymentHistoryRows = useMemo(
    () =>
      paymentHistory.map((item) => ({
        id: item.id,
        date: item.date,
        payment_mode: PAYMENT_MODE_LABELS[Number(item.payment_mode)] || "-",
        upi_transaction_id: item.upi_transaction_id || "-",
        paid: Number(item.paid_amount) || 0,
        balance: Number(item.balance_amount) || 0,
        status: Number(item.status),
        receipt_url: item.receipt_path || "",
      })),
    [paymentHistory],
  );

  const handleCancel = () => navigate(-1);

  const hasDuesPending = paymentSummary.totalAmountToPay > 0;

  const handleProceedToPay = () => {
    if (!hasDuesPending) return;

    if (formData.paymentMode === "cash") {
      const err = validateCashAmount(cashAmount);
      if (err) {
        setAmountError(err);
        return;
      }
      setCashModalOpen(true);
    } else {
      setPayModalOpen(true);
    }
  };
  /* ── EDIT 4: cash submit handler ──────────────────────────
     Cash collects the FULL outstanding balance at the desk, so
     there is no amount input, no UPI reference and no upload.
     Reuses schoolFees/feespayment with the cash payment_mode.
     ──────────────────────────────────────────────────────── */
  const handleCashCollect = async (paidFromModal) => {
    // prefer the amount the modal passed; fall back to the input
    const paidAmount = Number(paidFromModal ?? cashAmount) || 0;

    // guard: 0 < paid <= outstanding
    if (paidAmount <= 0 || paidAmount > paymentSummary.totalAmountToPay) {
      setModalState({
        open: true,
        type: "error",
        message: `Enter an amount between ₹1 and ${formatCurrency(
          paymentSummary.totalAmountToPay,
        )}.`,
      });
      return;
    }

    setSubmitting(true);
    try {
      const response = await AxiosInstance.post("schoolFees/feespayment", {
        class_id:
          Number(clientData?.class_id) || studentDetails.class_id || null,
        section_id: feeData?.section || "",
        school_id: Number(school_id),
        academic_year_id: Number(academic_year_id),
        student_id: Number(student_id),
        fees_group_id: Number(feeData?.fees_group_id) || null,
        upi_transaction_id: null,
        payment_file_path: "",
        payment_original_path: "",
        total_amount: paymentSummary.totalFees,
        total_discount: paymentSummary.discountApplied,
        net_payable: paymentSummary.netPayable,
        paid_amount: paidAmount, // <- the typed 1000, not the full 2900
        payment_mode: PAYMENT_MODE_VALUES.cash,
        order_id: null,
        trans_id: null,
        created_by: user?.id,
      });

      if (response?.data?.status) {
        setCashModalOpen(false);
        setModalState({
          open: true,
          type: "success",
          message:
            response?.data?.message ||
            `Payment of ${formatCurrency(paidAmount)} collected successfully!`,
        });
        fetchFeeDetails();
        fetchPaymentHistory();
      } else {
        setModalState({
          open: true,
          type: "error",
          message: response?.data?.message || "Payment could not be recorded.",
        });
      }
    } catch (error) {
      console.error("Error collecting cash payment:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          error?.response?.data?.message ||
          "Payment could not be recorded. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };
  const handlePaySubmit = async ({
    upiTransactionId,
    amountPayingNow,
    paymentImage,
  }) => {
    // defence in depth: the modal validates, but don't post an
    // over-payment even if it somehow gets here.
    const paidAmount = Math.min(
      Number(amountPayingNow) || 0,
      paymentSummary.totalAmountToPay,
    );
    if (paidAmount <= 0) return;

    setSubmitting(true);
    try {
      let paymentFilePath = "";
      let paymentOriginalPath = "";

      if (paymentImage) {
        const uploadPayload = new FormData();
        uploadPayload.append("content_image", paymentImage);
        uploadPayload.append("type", "payment");
        uploadPayload.append("school_id", school_id);

        let uploadResponse;
        try {
          uploadResponse = await AxiosInstance.post(
            "/content/upload",
            uploadPayload,
            { headers: { "Content-Type": "multipart/form-data" } },
          );
        } catch (uploadError) {
          console.error("Error uploading payment image:", uploadError);
          setModalState({
            open: true,
            type: "error",
            message:
              labels.onlyJpegPngAllowed || "Only jpeg, png files are allowed",
          });
          return;
        }

        const uploadedFile = Array.isArray(uploadResponse?.data)
          ? uploadResponse.data[0]
          : null;
        if (!uploadedFile) {
          throw new Error("Invalid upload response");
        }
        paymentFilePath = uploadedFile.upload_path;
        paymentOriginalPath = uploadedFile.original_name;
      }

      const response = await AxiosInstance.post("schoolFees/feespayment", {
        class_id:
          Number(clientData?.class_id) || studentDetails.class_id || null,
        section_id: feeData?.section || "",
        school_id: Number(school_id),
        academic_year_id: Number(academic_year_id),
        student_id: Number(student_id),
        fees_group_id: Number(feeData?.fees_group_id) || null,
        upi_transaction_id: upiTransactionId,
        payment_file_path: paymentFilePath,
        payment_original_path: paymentOriginalPath,
        total_amount: paymentSummary.totalFees,
        total_discount: paymentSummary.discountApplied,
        net_payable: paymentSummary.netPayable,
        paid_amount: paidAmount,
        // was: payment_mode: 0  -> cash payments logged as Online
        payment_mode: PAYMENT_MODE_VALUES[formData.paymentMode] ?? 0,
        order_id: null,
        trans_id: null,
        created_by: user?.id,
      });

      if (response?.data?.status) {
        setPayModalOpen(false);
        setModalState({
          open: true,
          type: "success",
          message:
            labels.paymentInitiatedSuccess ||
            `Payment of ${formatCurrency(paidAmount)} submitted successfully!`,
        });
        fetchFeeDetails();
        fetchPaymentHistory();
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response?.data?.message ||
            labels.paymentFailed ||
            "Payment could not be recorded.",
        });
      }
    } catch (error) {
      console.error("Error submitting payment:", error);
      setModalState({
        open: true,
        type: "error",
        message:
          labels.paymentFailed ||
          "Payment could not be recorded. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadReceipt = async (row) => {
    // Ignore clicks while a download is already in progress (same row
    // or another) so repeated clicks can't stack up multiple print jobs.
    if (!row?.id || downloadingId) return;
    setDownloadingId(row.id);

    let iframe = null;
    // Chrome names the saved PDF after the top document's title, so swap
    // it while the print dialog is open and restore it afterwards.
    const prevTitle = document.title;
    const removeIframe = () => {
      document.title = prevTitle;
      if (iframe && iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
        iframe = null;
      }
      setDownloadingId(null);
    };

    try {
      const response = await AxiosInstance.get(
        `schoolFees/download-receipt/${row.id}/1`,
        { responseType: "arraybuffer" },
      );
      if (!response?.data || response.data.byteLength === 0) {
        throw new Error("Empty receipt response");
      }
      let html = new TextDecoder("utf-8").decode(response.data);

      html = html.replace(
        "font-size:12px; opacity:0.9;",
        "font-size:12px; opacity:0.9; font-weight:normal;",
      );

      const printCss =
        "<style>" +
        // margin: 0 stops Chrome reserving space for its own date/title/url/page-number
        // header & footer; the 12mm page margin is recreated as body padding instead.
        "@page { size: A4 portrait; margin: 0; }" +
        "body { margin: 0; padding: 12mm; box-sizing: border-box; }" +
        // force the coloured header background to actually print
        "html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }" +
        "</style>";
      const htmlToPrint = html.includes("</head>")
        ? html.replace("</head>", `${printCss}</head>`)
        : printCss + html;

      iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);

      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(htmlToPrint);
      iframeDoc.close();

      // Wait for the iframe document to finish laying out before printing.
      await new Promise((resolve) => {
        if (iframeDoc.readyState === "complete") {
          resolve();
        } else {
          iframe.onload = () => resolve();
        }
        // hard fallback so we never hang if load never fires
        setTimeout(resolve, 1200);
      });

      const printWindow = iframe.contentWindow;
      // Clean up once the print dialog closes (Save or Cancel).
      printWindow.onafterprint = removeIframe;
      // Fallback cleanup in case onafterprint never fires.
      setTimeout(removeIframe, 60000);

      // PDF file name: Fee_Receipt_<receipt no> (receipt no from the HTML)
      const receiptNo = (html.match(/REP-\d+/) || [])[0] || "";
      const pdfTitle = receiptNo
        ? `Fee_Receipt_${receiptNo}`
        : "School_Fee_Receipt";
      document.title = pdfTitle;
      if (iframeDoc && iframeDoc.title !== undefined) {
        iframeDoc.title = pdfTitle;
      }

      printWindow.focus();
      printWindow.print();
    } catch (error) {
      console.error("Error downloading receipt:", error);
      removeIframe();
      setModalState({
        open: true,
        type: "error",
        message:
          labels.receiptDownloadFailed ||
          "Could not download the receipt. Please try again.",
      });
    }
  };

  const SectionTitle = ({ children }) => (
    <div className={styles.header}>
      <div className={styles.headerIcon}>
        <FaBuilding />
      </div>
      <h2 className={styles.headerTitle}>{children}</h2>
    </div>
  );

  const paymentHistoryColumns = useMemo(
    () => [
      {
        accessorKey: "date",
        header: labels.date || "Date",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "payment_mode",
        header: labels.paymentMode || "Payment Mode",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "upi_transaction_id",
        header: labels.upiTransactionId || "UPI Transaction ID",
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "paid",
        header: labels.paid || "Paid",
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => formatCurrency(cell.getValue()),
      },
      {
        accessorKey: "balance",
        header: labels.balance || "Balance",
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => formatCurrency(cell.getValue()),
      },
      {
        accessorKey: "status",
        header: labels.status || "Status",
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const statusValue = cell.getValue();
          return (
            <span
              style={{
                color: PAYMENT_STATUS_COLORS[statusValue] || "inherit",
                fontWeight: 600,
              }}
            >
              {PAYMENT_STATUS_LABELS[statusValue] || "-"}
            </span>
          );
        },
      },
      {
        id: "receipt",
        header: labels.downloadReceipt || "Download Receipt",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <CustomButton
            type="button"
            variant="contained"
            className={payStyles.receiptButton}
            fullWidth={false}
            // disabled={!row.original.receipt_url}
            disabled={downloadingId !== null}
            loading={downloadingId === row.original.id}
            onClick={() => handleDownloadReceipt(row.original)}
          >
            <FaDownload style={{ marginRight: "6px" }} />
            PDF
          </CustomButton>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [labels, downloadingId],
  );

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={
          modalState.type === "success"
            ? labels.success || "Success"
            : labels.failed || "Failed"
        }
        message={modalState.message}
        onOk={() => setModalState((prev) => ({ ...prev, open: false }))}
      />

      <PayOnlineModal
        open={payModalOpen}
        onClose={() => setPayModalOpen(false)}
        onSubmit={handlePaySubmit}
        submitting={submitting}
        schoolId={school_id}
        totalAmount={paymentSummary.totalAmountToPay}
        paymentMode={formData.paymentMode === "cash" ? "Cash" : "Online"}
        student={{
          name: studentDetails.student_name,
          admissionId: studentDetails.admission_id,
          classSection: [studentDetails.class_name, studentDetails.section]
            .filter(Boolean)
            .join("-"),
        }}
      />
      <PayOfflineModal
        open={cashModalOpen}
        onClose={() => setCashModalOpen(false)}
        onConfirm={handleCashCollect}
        submitting={submitting}
        amount={Number(cashAmount) || 0}
        totalDue={paymentSummary.totalAmountToPay}
        paymentMode="Cash"
        student={{
          name: studentDetails.student_name,
          admissionId: studentDetails.admission_id,
          classSection: [studentDetails.class_name, studentDetails.section]
            .filter(Boolean)
            .join("-"),
        }}
      />
      <div className={styles.container}>
        {/* Student Details */}
        <SectionTitle>
          {labels.studentDetails || "Student Details"}
        </SectionTitle>
        <div className={styles.viewFormGrid}>
          <CustomTextInput
            label={labels.admissionId || "Admission ID"}
            name="admission_id"
            value={studentDetails.admission_id}
            onChange={() => {}}
            disabled
            readOnly
          />
          <CustomTextInput
            label={labels.studentName || "Student Name"}
            name="student_name"
            value={studentDetails.student_name}
            onChange={() => {}}
            disabled
            readOnly
          />
          <CustomTextInput
            label={labels.classLabel || "Class"}
            name="class_name"
            value={studentDetails.class_name}
            onChange={() => {}}
            disabled
            readOnly
          />
          <CustomTextInput
            label={labels.sectionLabel || "Section"}
            name="section"
            value={studentDetails.section}
            onChange={() => {}}
            disabled
            readOnly
          />
        </div>

        {/* Fees Details */}
        <div className={styles.sectionSpacing}>
          <SectionTitle>{labels.feesDetails || "Fees Details"}</SectionTitle>
          <div className={payStyles.feesTableWrap}>
            <table className={payStyles.feesTable}>
              <thead>
                <tr>
                  <th>{labels.feeType || "Fee Type"}</th>
                  <th className={payStyles.amountCol}>
                    {labels.amountRs || "Amount (₹)"}
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={2} className={payStyles.emptyRow}>
                      {labels.loading || "Loading..."}
                    </td>
                  </tr>
                )}
                {!loading && feesRows.length === 0 && (
                  <tr>
                    <td colSpan={2} className={payStyles.emptyRow}>
                      {labels.noFeesFound || "No fees found."}
                    </td>
                  </tr>
                )}
                {feesRows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.fee_type}</td>
                    <td className={payStyles.amountCol}>
                      {formatCurrency(row.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Payment Summary */}
        <div className={styles.sectionSpacing}>
          <SectionTitle>
            {labels.paymentSummary || "Payment summary"}
          </SectionTitle>
          <div className={payStyles.summaryPanel}>
            <div className={payStyles.summaryRow}>
              <span className={payStyles.summaryLabel}>
                {labels.dueDate || "Due Date"}
              </span>
              <span className={payStyles.summaryValueBlue}>
                {paymentSummary.dueDate}
              </span>
            </div>
            <div className={payStyles.summaryRow}>
              <span className={payStyles.summaryLabel}>
                {labels.totalFees || "Total Fees"}
              </span>
              <span className={payStyles.summaryValueBold}>
                {formatCurrency(paymentSummary.totalFees)}
              </span>
            </div>
            <div className={payStyles.summaryRow}>
              <span className={payStyles.summaryLabel}>
                {labels.discountApplied || "Discount Applied"}
              </span>
              <span className={payStyles.summaryValueNegative}>
                - {formatCurrency(paymentSummary.discountApplied)}
              </span>
            </div>
            <div
              className={`${payStyles.summaryRow} ${payStyles.summaryDivider}`}
            >
              <span className={payStyles.summaryLabelBold}>
                {labels.netPayable || "Net Payable"}
              </span>
              <span className={payStyles.summaryValueBold}>
                {formatCurrency(paymentSummary.netPayable)}
              </span>
            </div>
            <div className={payStyles.summaryRow}>
              <span className={payStyles.summaryLabel}>
                {labels.fineAmount || "Fine"}
              </span>
              <span className={payStyles.summaryValue}>
                {formatCurrency(paymentSummary.fine)}
              </span>
            </div>
            <div className={payStyles.summaryRow}>
              <span className={payStyles.summaryLabel}>
                {labels.totalPaid || "Total Paid"}
              </span>
              <span className={payStyles.summaryValueNegative}>
                {formatCurrency(paymentSummary.totalPaid)}
              </span>
            </div>
            <div
              className={`${payStyles.summaryRow} ${payStyles.totalPayableRow}`}
            >
              <span className={payStyles.totalPayableLabel}>
                {labels.totalAmountToPay || "Total Amount to be Paid"}
              </span>
              <span className={payStyles.totalPayableValue}>
                {formatCurrency(paymentSummary.totalAmountToPay)}
              </span>
            </div>
          </div>
          {slug === "group-administrator" ? (
            <div className={payStyles.paymentModeSection}>
              <SectionTitle>
                {labels.paymentMode || "Payment Mode"}
              </SectionTitle>

              {/* radios */}
              <div className={payStyles.paymentModeOptions}>
                <label className={payStyles.radioLabel}>
                  <input
                    type="radio"
                    name="paymentMode"
                    value="cash"
                    className={payStyles.radioInput}
                    checked={formData.paymentMode === "cash"}
                    onChange={() =>
                      setFormData((prev) => ({ ...prev, paymentMode: "cash" }))
                    }
                  />
                  {labels.cash || "Cash"}
                </label>

                <label className={payStyles.radioLabel}>
                  <input
                    type="radio"
                    name="paymentMode"
                    value="online"
                    className={payStyles.radioInput}
                    checked={formData.paymentMode === "online"}
                    onChange={() =>
                      setFormData((prev) => ({
                        ...prev,
                        paymentMode: "online",
                      }))
                    }
                  />
                  {labels.online || "Online"}
                </label>
              </div>

              {/* Amount input — CASH ONLY. Hidden entirely for Online. */}
              {formData.paymentMode === "cash" && (
                <div className={payStyles.amountField}>
                  <label className={payStyles.amountLabel}>
                    {labels.amountPayingNow || "Amount paying now"}
                  </label>
                  <input
                    type="text"
                    inputMode="decimal"
                    className={payStyles.amountInput}
                    value={cashAmount}
                    onChange={handleCashAmountChange}
                    placeholder={formatCurrency(
                      paymentSummary.totalAmountToPay,
                    )}
                  />
                  {amountError && (
                    <span className={payStyles.amountErrorText}>
                      {amountError}
                    </span>
                  )}
                </div>
              )}
              {/* Buttons — own full-width centered row */}
              <div className={payStyles.actionRow}>
                <CustomButton
                  type="button"
                  variant="contained"
                  className={styles.cancelButton}
                  fullWidth={false}
                  onClick={handleCancel}
                >
                  {labels.cancel || "Cancel"}
                </CustomButton>

                <CustomButton
                  type="button"
                  variant="contained"
                  className={styles.addButton}
                  fullWidth={false}
                  disabled={!hasDuesPending}
                  onClick={handleProceedToPay}
                >
                  {labels.savePrint || "Save & Print Receipt"}
                </CustomButton>
              </div>
            </div>
          ) : (
            <div className={styles.reminderFooter}>
              <CustomButton
                type="button"
                variant="contained"
                className={styles.cancelButton}
                fullWidth={false}
                onClick={handleCancel}
              >
                {labels.cancel || "Cancel"}
              </CustomButton>
              <CustomButton
                type="button"
                variant="contained"
                className={styles.addButton}
                fullWidth={false}
                disabled={!hasDuesPending}
                onClick={handleProceedToPay}
              >
                {labels.proceedToPay || "Proceed to Pay"}
              </CustomButton>
            </div>
          )}
        </div>

        {/* Payment History */}
        <div className={styles.sectionSpacing}>
          <SectionTitle>
            {labels.paymentHistory || "Payment history"}
          </SectionTitle>
          <CustomMRTTable
            columns={paymentHistoryColumns}
            data={paymentHistoryRows}
            pagination={pagination}
            onPaginationChange={setPagination}
            manualPagination={false}
            enableSearch={false}
            enableFiltering={false}
          />
        </div>
      </div>
    </>
  );
};

export default PayFees;
